import { useEffect, useMemo, useState } from "react";
import posthog from "posthog-js";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { ChipStrip } from "./components/ChipStrip";
import { LocationCard } from "./components/LocationCard";
import { PromoCard } from "./components/PromoCard";
import { MapView } from "./components/MapView";
import { Drawer } from "./components/Drawer";
import { SiteFooter } from "./components/SiteFooter";
import { searchLocations } from "./lib/api";
import type { AppId, Location, SearchFilters } from "./lib/types";

export default function App() {
  const [appId, setAppId] = useState<AppId>("mgo-web");
  const [viewMode, setViewMode] = useState<"customer" | "internal">("customer");
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<SearchFilters>({});
  const [results, setResults] = useState<Location[]>([]);
  const [selected, setSelected] = useState<Location | null>(null);
  const [drawer, setDrawer] = useState<Location | null>(null);
  const [mobileView, setMobileView] = useState<"list" | "map">("list");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Run search whenever filters / query / appId change. State updates live in
  // an async helper so they are not called synchronously in the effect body.
  useEffect(() => {
    const ctrl = new AbortController();
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const res = await searchLocations(
          { ...filters, query },
          { appId, signal: ctrl.signal }
        );
        setResults(res.results);
        posthog.capture("locator_search", {
          query,
          app_id: appId,
          results_count: res.results.length,
          filters: Object.keys(filters).filter((k) => !!filters[k as keyof SearchFilters]),
        });
      } catch (e) {
        const err = e as { name?: string; message?: string };
        if (err?.name !== "AbortError") setError(String(err?.message ?? e));
      } finally {
        setLoading(false);
      }
    }
    run();
    return () => ctrl.abort();
  }, [appId, filters, query]);

  // Derive a city label for the hero
  const cityLabel = useMemo(() => {
    if (query.trim().length > 1) return query.trim();
    if (!results.length) return "your area";
    const counts: Record<string, number> = {};
    results.forEach((r) => {
      const c = r.address?.city;
      if (c) counts[c] = (counts[c] ?? 0) + 1;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : "your area";
  }, [results, query]);

  // Auto-clear selection when it falls out of the current results — derived
  // during render instead of in an effect to avoid calling setState inside an
  // effect (react-hooks/set-state-in-effect).
  if (
    selected &&
    !results.find((r) => r.location_id === selected.location_id)
  ) {
    setSelected(null);
  }

  return (
    <div className="min-h-full flex flex-col bg-white">
      <Header
        appId={appId}
        setAppId={setAppId}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      <Hero
        query={query}
        setQuery={setQuery}
        results={results}
        cityLabel={cityLabel}
      />
      <ChipStrip filters={filters} setFilters={setFilters} />

      {viewMode === "internal" && (
        <div className="bg-slate-900 text-slate-100 text-xs font-mono px-4 py-2 border-y border-slate-700 flex items-center gap-4 overflow-x-auto">
          <span className="text-emerald-300">GET</span>
          <span>
            {(import.meta.env.VITE_UL_API_BASE || "(mock)") +
              "/v1/locations/search"}
          </span>
          <span className="text-slate-400">X-App-Id: {appId}</span>
          <span className="text-slate-400">
            results: {results.length}
            {loading ? " · loading…" : ""}
            {error ? ` · ${error}` : ""}
          </span>
        </div>
      )}

      {/* Mobile-only List/Map toggle */}
      <div className="md:hidden flex border-b border-slate-200 bg-white shrink-0">
        <button
          onClick={() => setMobileView("list")}
          className={`flex-1 py-2.5 text-sm font-medium border-b-2 ${
            mobileView === "list"
              ? "text-mg-red border-mg-red"
              : "border-transparent text-slate-500"
          }`}
        >
          ☰ List ({results.length})
        </button>
        <button
          onClick={() => setMobileView("map")}
          className={`flex-1 py-2.5 text-sm font-medium border-b-2 ${
            mobileView === "map"
              ? "text-mg-red border-mg-red"
              : "border-transparent text-slate-500"
          }`}
        >
          ⌖ Map
        </button>
      </div>

      <div
        className="md:grid md:grid-cols-[480px_1fr] min-h-0"
        style={{ height: "70vh" }}
      >
        <div
          className={`border-r border-slate-200 bg-white overflow-hidden flex-col min-h-0 ${
            mobileView === "list" ? "flex" : "hidden"
          } md:flex`}
        >
          <div className="hidden md:flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-slate-500 bg-white border-b border-slate-100 shrink-0">
            <span>{results.length} results</span>
            <span className="font-mono normal-case text-slate-400">{appId}</span>
          </div>
          <div className="flex-1 overflow-y-auto scroll-list px-3 py-3">
            {results.length > 0 && (
              <PromoCard
                kind="deal"
                partner="Walmart"
                title="Limited time deals"
                subtitle="Better rates when you send or receive at Walmart this month"
              />
            )}
            {results.length === 0 && !loading ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                No locations match your filters. Try clearing filters or
                expanding the radius.
              </div>
            ) : (
              results.map((loc, idx) => (
                <div key={loc.location_id}>
                  <LocationCard
                    loc={loc}
                    selected={selected?.location_id === loc.location_id}
                    onClick={() => {
                      setSelected(loc);
                      posthog.capture("locator_location_selected", {
                        location_id: loc.location_id,
                        name: loc.name,
                        city: loc.address?.city,
                        country: loc.address?.country,
                        app_id: appId,
                        result_position: idx + 1,
                      });
                      if (window.matchMedia("(max-width: 767px)").matches) {
                        setMobileView("map");
                      }
                    }}
                    onView={(loc) => {
                      setDrawer(loc);
                      posthog.capture("locator_detail_viewed", {
                        location_id: loc.location_id,
                        name: loc.name,
                        city: loc.address?.city,
                        country: loc.address?.country,
                        app_id: appId,
                      });
                    }}
                  />
                  {idx === 2 && results.length > 4 && (
                    <PromoCard
                      kind="referral"
                      title="Give $20, Get $20"
                      subtitle="Don't let your friends miss out on great rates."
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        <div
          className={`relative h-full min-h-0 ${
            mobileView === "map" ? "block" : "hidden"
          } md:block`}
        >
          <MapView
            locations={results}
            selected={selected}
            setSelected={setSelected}
          />
        </div>
      </div>

      <Drawer loc={drawer} onClose={() => setDrawer(null)} />
      <SiteFooter />
    </div>
  );
}
